import React from "react";
import { ASSETS } from "../lib/brand";
import { Link } from "react-router-dom";
import { Heart, Star, ArrowRight, Quote, Home as HomeIcon, GraduationCap, KeyRound, Calendar } from "lucide-react";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Our Story</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          A community<br />
          built on the belief that<br />
          <span className="brush-underline brush-green">every star shines.</span>
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-start py-12">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <p className="text-lg text-[#4B5563] leading-relaxed">
            Stars of Autism was founded to ensure that families in Marion, Arkansas have a dedicated space where they
            can find the support they need to thrive — beyond the classroom, beyond a single diagnosis, beyond what the
            world tries to tell them they cannot do.
          </p>
          <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">
            We operate under the philosophy that every individual is unique and valuable. By providing resources and
            fostering community, we help families navigate the challenges of the Autism spectrum together — not alone.
          </p>
          <div className="mt-8 rounded-3xl bg-[#F4F1EA] border border-black/5 p-7">
            <div className="font-heading text-2xl font-semibold text-[#1F2937]">"Different, not less."</div>
            <div className="text-sm text-[#6B7280] mt-1">— Our guiding philosophy</div>
          </div>
        </div>

        {/* Chatoria + daughter photo card */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -top-5 -left-5 w-24 h-24 rounded-full bg-[#FFB300]/25 blur-2xl" />
            <div className="absolute -bottom-5 -right-5 w-28 h-28 rounded-full bg-[#E53935]/25 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-black/5 bg-white p-3 shadow-sm">
              <img src={ASSETS.chatoria} alt="Chatoria Franklin with her daughter"
                   className="w-full h-auto rounded-[1.6rem] object-cover" />
            </div>
            <div className="mt-4 rounded-2xl bg-white border border-black/5 px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E53935]/15 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#E53935] fill-current" strokeWidth={0} />
              </div>
              <div className="text-sm leading-snug">
                <span className="font-semibold text-[#1F2937]">Chatoria & her daughter</span>
                <div className="text-[#6B7280]">The reason Stars of Autism exists.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-[#1F2937] text-white p-10 relative overflow-hidden h-full">
              <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-[#E53935]/25 blur-3xl" />
              <div className="text-xs uppercase tracking-[0.2em] text-[#FFB300] font-semibold">Founder & Director</div>
              <h3 className="mt-2 font-heading text-4xl font-semibold tracking-tight">Chatoria Franklin</h3>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white/85" data-testid="founder-service-area">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB300]" />
                Serving Marion & surrounding cities
              </div>
              <Quote className="w-6 h-6 text-[#FFB300] mt-6" strokeWidth={1.6} />
              <p className="mt-3 text-white/85 leading-relaxed italic">
                "I started Stars of Autism because my daughter taught me what 'different, not less' really means.
                Every family in Marion deserves a place where their child is celebrated — not corrected."
              </p>
              <Link to="/contact" data-testid="about-contact-director-btn"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[#1F2937] px-6 py-3 text-sm font-semibold hover:bg-[#FFB300]">
                Write to Chatoria <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">What We Believe</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F2937]">
              Behavior is communication. Belonging is medicine.
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { c: "#2196F3", t: "Inclusion", d: "Every ability welcomed, every story heard." },
                { c: "#E53935", t: "Compassion", d: "We meet families exactly where they are." },
                { c: "#FFB300", t: "Education", d: "Knowledge that empowers, never overwhelms." },
                { c: "#4CAF50", t: "Joy", d: "Because joy is a need, not a luxury." },
              ].map((v) => (
                <div key={v.t} className="rounded-2xl bg-white border border-black/5 p-5">
                  <div className="flex items-center gap-3">
                    <Star className="w-4 h-4 fill-current" strokeWidth={0} style={{ color: v.c }} />
                    <div className="font-heading font-semibold text-[#1F2937]">{v.t}</div>
                  </div>
                  <div className="mt-2 text-sm text-[#6B7280]">{v.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Goal Timeline */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16" data-testid="about-goal-timeline">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Our Roadmap</div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1F2937]">
          Goal timeline<br />
          <span className="brush-underline brush-blue">for the next 1-3 years.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-[#4B5563] leading-relaxed">
          Every puzzle piece we place moves us closer to these three foundational goals for
          our community in Marion.
        </p>

        <div className="mt-10 relative">
          {/* Vertical connector line for md+ */}
          <div className="hidden md:block absolute left-0 right-0 top-14 h-0.5 bg-gradient-to-r from-[#2196F3] via-[#FFB300] to-[#4CAF50] opacity-40" aria-hidden />
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                num: 1,
                icon: HomeIcon,
                color: "#2196F3",
                title: "Purchase a Facility",
                blurb: "Secure a permanent, brick-and-mortar home in Marion where families can access resources beyond the school system.",
              },
              {
                num: 2,
                icon: GraduationCap,
                color: "#FFB300",
                title: "Develop Agencies, Outsourced Programs & Schools",
                blurb: "Build partnerships with therapy providers, community programs, and inclusive schools so families have a full support network.",
              },
              {
                num: 3,
                icon: KeyRound,
                color: "#4CAF50",
                title: "Independent Living Housing",
                blurb: "Create supported, dignified housing options so individuals with Autism can thrive with independence into adulthood.",
              },
            ].map((g) => {
              const Icon = g.icon;
              return (
                <div
                  key={g.num}
                  data-testid={`goal-${g.num}`}
                  className="relative rounded-3xl bg-white border border-black/5 p-7 hover:-translate-y-0.5 hover:shadow-sm transition"
                >
                  {/* Number chip on the timeline */}
                  <div className="relative z-10 flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-2xl font-heading font-bold text-white flex items-center justify-center shrink-0"
                      style={{ backgroundColor: g.color }}
                    >
                      {g.num}
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em]"
                         style={{ backgroundColor: `${g.color}15`, color: g.color, border: `1px solid ${g.color}44` }}>
                      <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
                      1-3 years
                    </div>
                  </div>

                  <div className="mt-5 flex items-start gap-3">
                    <Icon className="w-5 h-5 mt-1 shrink-0" style={{ color: g.color }} strokeWidth={1.6} />
                    <h3 className="font-heading text-xl font-semibold text-[#1F2937] leading-snug">{g.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{g.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24">
        <div className="rounded-3xl bg-[#F4F1EA] paper-grain p-10 md:p-14 text-center border border-black/5">
          <Heart className="w-7 h-7 text-[#E53935] mx-auto" strokeWidth={1.6} />
          <h3 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F2937]">
            Be part of the puzzle.
          </h3>
          <p className="mt-3 max-w-xl mx-auto text-[#4B5563]">
            Your support helps us build a permanent home for our community.
          </p>
          <Link to="/donate" data-testid="about-cta-donate-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-7 py-3.5 text-sm font-semibold">
            Donate now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
